import type React from "react";
import { useState } from "react";
import * as S from "../styles";
import type { DashboardFormData } from "../types";
import {
	LANGUAGE_OPTIONS,
	PAST_SEMESTER_MSG,
	SEMESTER_OPTIONS,
	adjustSemesterForYear,
	getCurrentSemester,
	getSemesterLabel,
	isPastSemester,
} from "../hooks/useDashboard";

const REQUIRED_MSG = "필수 항목(*)을 입력해 주세요.";

interface CreateSectionModalProps {
	isOpen: boolean;
	onClose: () => void;
	formData: DashboardFormData;
	setFormData: React.Dispatch<React.SetStateAction<DashboardFormData>>;
	onSubmit: () => void;
}

const CreateSectionModal: React.FC<CreateSectionModalProps> = ({
	isOpen,
	onClose,
	formData,
	setFormData,
	onSubmit,
}) => {
	const [errors, setErrors] = useState<Record<string, boolean>>({});

	if (!isOpen) return null;

	const titleTrimmed = formData.courseTitle?.toString().trim() ?? "";
	const yearVal =
		formData.year != null && formData.year !== ""
			? Number(formData.year)
			: Number.NaN;
	const minYear = getCurrentSemester().year;
	const yearOk = !Number.isNaN(yearVal) && yearVal >= 2020 && yearVal <= 2099;
	const semesterOk = yearOk && !isPastSemester(yearVal, formData.semester);
	const hasTitle = titleTrimmed.length > 0;

	const handleSubmit = () => {
		const errs: Record<string, boolean> = {};
		if (!hasTitle) errs.courseTitle = true;
		if (!yearOk) errs.year = true;
		else if (!semesterOk) errs.semester = true;
		setErrors(errs);
		if (Object.keys(errs).length > 0) return;
		onSubmit();
	};

	const hasRequiredErrors = Boolean(errors.courseTitle || errors.year);
	// 년도를 바꿔 이미 고른 학기가 지난 학기가 된 경우에도 바로 안내
	const showPastSemester = yearOk && !semesterOk;

	return (
		<S.ModalOverlay onClick={onClose}>
			<S.ModalContent onClick={(e) => e.stopPropagation()}>
				<S.ModalHeader>
					<h2 style={{ color: "white" }}>새 수업 만들기</h2>
					<S.ModalClose onClick={onClose}>×</S.ModalClose>
				</S.ModalHeader>
				<S.ModalBody>
					{hasRequiredErrors && (
						<S.RequiredMessage role="alert">{REQUIRED_MSG}</S.RequiredMessage>
					)}
					{showPastSemester && (
						<S.RequiredMessage role="alert">
							{PAST_SEMESTER_MSG}
						</S.RequiredMessage>
					)}
					<S.FormGroup>
						<label htmlFor="create-section-course-title">강의 제목 *</label>
						<S.FormInput
							id="create-section-course-title"
							type="text"
							value={formData.courseTitle}
							onChange={(e) => {
								setErrors((prev) => ({ ...prev, courseTitle: false }));
								setFormData((prev) => ({
									...prev,
									courseTitle: e.target.value,
								}));
							}}
							placeholder="예: 자바프로그래밍"
							style={
								errors.courseTitle ? { borderColor: "#dc2626" } : undefined
							}
						/>
					</S.FormGroup>
					<S.FormGroup>
						<label htmlFor="create-section-description">수업 설명</label>
						<S.FormTextarea
							id="create-section-description"
							value={formData.description}
							onChange={(e) =>
								setFormData((prev) => ({
									...prev,
									description: e.target.value,
								}))
							}
							placeholder="수업에 대한 설명을 입력하세요 (선택사항)"
							rows={3}
						/>
					</S.FormGroup>
					<S.FormRow>
						<S.FormGroup>
							<label htmlFor="create-section-year">년도 *</label>
							<S.FormInput
								id="create-section-year"
								type="number"
								value={formData.year}
								onChange={(e) => {
									setErrors((prev) => ({ ...prev, year: false }));
									const year = e.target.value;
									setFormData((prev) => ({
										...prev,
										year,
										semester: adjustSemesterForYear(year, prev.semester),
									}));
								}}
								placeholder={String(minYear)}
								min={minYear}
								max={2099}
								style={errors.year ? { borderColor: "#dc2626" } : undefined}
							/>
						</S.FormGroup>
						<S.FormGroup>
							<label htmlFor="create-section-semester">구분 *</label>
							<S.FormSelect
								id="create-section-semester"
								value={formData.semester}
								onChange={(e) => {
									setErrors((prev) => ({ ...prev, semester: false }));
									setFormData((prev) => ({ ...prev, semester: e.target.value }));
								}}
								style={
									showPastSemester ? { borderColor: "#dc2626" } : undefined
								}
							>
								{SEMESTER_OPTIONS.filter(
									(semester) => !yearOk || !isPastSemester(yearVal, semester),
								).map((semester) => (
									<option key={semester} value={semester}>
										{getSemesterLabel(semester)}
									</option>
								))}
							</S.FormSelect>
						</S.FormGroup>
					</S.FormRow>
					<S.FormGroup>
						<label htmlFor="create-section-language">사용 언어 *</label>
						<S.FormSelect
							id="create-section-language"
							value={formData.language}
							onChange={(e) =>
								setFormData((prev) => ({ ...prev, language: e.target.value }))
							}
						>
							{LANGUAGE_OPTIONS.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</S.FormSelect>
						<S.FormHint>
							과제·코딩테스트는 이 언어로만 풀 수 있어요. 수업을 만든 뒤에는 바꿀 수 없어요.
						</S.FormHint>
					</S.FormGroup>
				</S.ModalBody>
				<S.ModalFooter>
					<S.BtnCancel onClick={onClose}>취소</S.BtnCancel>
					<S.BtnSubmit
						type="button"
						onClick={handleSubmit}
						disabled={!hasTitle || !yearOk || !semesterOk}
					>
						생성하기
					</S.BtnSubmit>
				</S.ModalFooter>
			</S.ModalContent>
		</S.ModalOverlay>
	);
};

export default CreateSectionModal;
